import { useCallback, useState } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Orden de tabla reutilizable por el kit (`TablaKit`).
 *
 * `useOrden` guarda la columna activa y su dirección; `ordenar` aplica ese orden
 * a cualquier arreglo con un mapa de accesores por `sortKey`. Se separan para que
 * las tablas agrupadas (varias `TablaKit`) compartan un único estado de orden y lo
 * apliquen a cada grupo.
 */
export type Direccion = "asc" | "desc";

export interface Orden {
  key: string;
  dir: Direccion;
}

export type Accesores<T> = Record<string, (item: T) => string | number | boolean | null | undefined>;

export function useOrden(inicial: Orden | null = null) {
  const [orden, setOrden] = useState<Orden | null>(inicial);
  // Clic en una columna: activa asc; si ya estaba activa, alterna asc/desc.
  const onSort = useCallback((key: string) => {
    setOrden((prev) => (prev?.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  }, []);
  return { orden, onSort };
}

/**
 * Lo mismo que `useOrden`, pero el orden vive en la URL (`?orden=periodo`,
 * `?orden=-periodo` para descendente).
 *
 * Por lo mismo que los filtros y la pestaña (ver `utils/estadoLista.ts`): al abrir
 * una ficha y volver, el navegador restaura la dirección y la tabla aparece ordenada
 * como se dejó. Reemplaza la entrada del historial: "Volver" sale de la página, no
 * deshace clic por clic.
 */
export function useOrdenUrl(clave = "orden") {
  const [params, setParams] = useSearchParams();
  const crudo = params.get(clave) ?? "";
  const orden: Orden | null = crudo ? (crudo.startsWith("-") ? { key: crudo.slice(1), dir: "desc" } : { key: crudo, dir: "asc" }) : null;

  const onSort = useCallback(
    (key: string) =>
      setParams(
        (previos) => {
          const siguientes = new URLSearchParams(previos);
          const actual = previos.get(clave) ?? "";
          // Clic en una columna: activa asc; si ya estaba activa, alterna asc/desc.
          siguientes.set(clave, actual === key ? `-${key}` : key);
          return siguientes;
        },
        { replace: true },
      ),
    [setParams, clave],
  );
  return { orden, onSort };
}

function comparar(a: string | number | boolean | null | undefined, b: string | number | boolean | null | undefined): number {
  // Vacíos siempre al final, sin importar la dirección.
  const va = a === null || a === undefined || a === "";
  const vb = b === null || b === undefined || b === "";
  if (va && vb) return 0;
  if (va) return 1;
  if (vb) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  if (typeof a === "boolean" && typeof b === "boolean") return a === b ? 0 : a ? -1 : 1;
  return String(a).localeCompare(String(b), "es", { numeric: true, sensitivity: "base" });
}

/** Devuelve una copia ordenada de `datos` según `orden`, o `datos` tal cual si no hay orden. */
export function ordenar<T>(datos: T[], accesores: Accesores<T>, orden: Orden | null): T[] {
  if (!orden) return datos;
  const acc = accesores[orden.key];
  if (!acc) return datos;
  const factor = orden.dir === "asc" ? 1 : -1;
  // Empates estables: si el accesor no discrimina, se conserva el orden previo.
  return datos
    .map((item, i) => [item, i] as const)
    .sort(([a, ia], [b, ib]) => comparar(acc(a), acc(b)) * factor || ia - ib)
    .map(([item]) => item);
}
